import React, { useCallback, useEffect, useMemo, useState } from "react";
import BodyWrapper from "../../components/Wrapper/BodyWrapper";
import Header from "../../components/Header/Header";
import FetchingLoading from "../../components/Loaders/FetchingLoading";
import LoanTransactionLogCards from "../../components/Loan/LoanTransactionLogCards";
import CustomTabs from "../../components/Tabs/CustomTabs";
import AuthInput from "../../components/Input/AuthInput";
import EmptyState from "../../components/ui/EmptyState";
import StatusBadge from "../../components/ui/StatusBadge";
import { FiInbox } from "react-icons/fi";
import { GetMyLoanApi } from "../../ApiRequests";
import { ErrorToast } from "../../utils/ShowToast";
import { usePageRefresh } from "../../hooks/usePageRefresh";
import {
  computeLoanRunningBalances,
  getLoanBalance,
  getLoanPaid,
  getLoanPrincipal,
  getLoanStatusLabel,
  getLoanTypeLabel,
} from "../../utils/LoanConverter";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const DETAIL_TABS = [
  { label: "Full log" },
  { label: "Loans given only" },
  { label: "Repayments only" },
];

const LoanPartyPortal = () => {
  const [account, setAccount] = useState(null);
  const [disbursements, setDisbursements] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [logSearch, setLogSearch] = useState("");

  const loadLoan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetMyLoanApi();
      const payload = res?.data?.data?.payload;
      setAccount(payload?.account || payload?.loan || null);
      setDisbursements(
        Array.isArray(payload?.disbursements) ? payload.disbursements : []
      );
      setPayments(Array.isArray(payload?.payments) ? payload.payments : []);
    } catch (e) {
      setAccount(null);
      setDisbursements([]);
      setPayments([]);
      ErrorToast(e.response?.data?.error?.msg || "Could not load your loan");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLoan();
  }, [loadLoan]);

  usePageRefresh(loadLoan);

  const logEntries = useMemo(() => {
    const givenRows = disbursements.map((row) => ({
      _id: `d-${row._id}`,
      entryType: "disbursement",
      sourceId: row._id,
      loanAccountId: account?._id,
      partyName: account?.partyName,
      phone: account?.phone,
      loanType: account?.loanType,
      amount: row.amount,
      date: row.date,
      accountName: row.accountId?.account_name || "—",
      marka: row.marka || "",
      bichak_no: row.bichak_no || "",
      desc: row.desc || "",
    }));
    const payRows = payments.map((row) => ({
      _id: `p-${row._id}`,
      entryType: "repayment",
      sourceId: row._id,
      loanAccountId: account?._id,
      partyName: account?.partyName,
      phone: account?.phone,
      loanType: account?.loanType,
      amount: row.amount,
      date: row.date,
      accountName: row.accountId?.account_name || "—",
      desc: row.desc || "",
    }));
    return [...givenRows, ...payRows].sort((a, b) => b.date - a.date);
  }, [account, disbursements, payments]);

  const tabEntries = useMemo(() => {
    if (activeTab === 1) {
      return logEntries.filter((r) => r.entryType === "disbursement");
    }
    if (activeTab === 2) {
      return logEntries.filter((r) => r.entryType === "repayment");
    }
    return logEntries;
  }, [logEntries, activeTab]);

  const searchedTabEntries = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    if (!q) return tabEntries;
    return tabEntries.filter(
      (row) =>
        String(row.desc || "").toLowerCase().includes(q) ||
        String(row.marka || "").toLowerCase().includes(q) ||
        String(row.bichak_no || "").toLowerCase().includes(q)
    );
  }, [tabEntries, logSearch]);

  const tabEntriesWithBalance = useMemo(() => {
    const balanceMap = computeLoanRunningBalances(logEntries);
    return searchedTabEntries.map((row) => ({
      ...row,
      balanceAfter: balanceMap.get(row._id) ?? 0,
    }));
  }, [logEntries, searchedTabEntries]);

  if (loading) {
    return (
      <BodyWrapper>
        <FetchingLoading />
      </BodyWrapper>
    );
  }

  if (!account) {
    return (
      <BodyWrapper>
        <Header title="My loan" desc="Read-only view" />
        <EmptyState
          icon={FiInbox}
          title="No loan account linked"
          description="No loan account is linked to your login. Please contact your branch."
        />
      </BodyWrapper>
    );
  }

  const balance = getLoanBalance(account);

  return (
    <BodyWrapper>
      <Header
        title="My loan"
        desc={`${account.partyName} · read-only`}
      />

      <div className="rounded-2xl border border-line bg-card shadow-card p-4 sm:p-5 mb-5">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <StatusBadge tone="danger">{getLoanTypeLabel(account)}</StatusBadge>
            <h2 className="text-xl font-bold mt-2 text-ink">{account.partyName}</h2>
            {account.phone && (
              <p className="text-sm text-inkSecondary">{account.phone}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-muted uppercase">Status</p>
            <p className="font-bold text-main">
              {getLoanStatusLabel(account)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mt-4 text-sm">
          <div className="rounded-lg bg-subtle p-3">
            <p className="text-muted text-xs">Total given</p>
            <p className="font-bold tabular-nums text-ink">
              Rs {fmtRs(getLoanPrincipal(account))}
            </p>
          </div>
          <div className="rounded-lg bg-successSoft p-3">
            <p className="text-success/80 text-xs">Received back</p>
            <p className="font-bold tabular-nums text-success">
              Rs {fmtRs(getLoanPaid(account))}
            </p>
          </div>
          <div className="rounded-lg bg-dangerSoft p-3 col-span-2 sm:col-span-1">
            <p className="text-danger/80 text-xs">Balance due</p>
            <p className="font-bold tabular-nums text-danger">
              Rs {fmtRs(balance)}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <CustomTabs
          tabs={DETAIL_TABS}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          scrollable
        />
      </div>

      <div className="mb-4">
        <AuthInput
          Value={logSearch}
          setValue={setLogSearch}
          placeholder="Search description, marka, bichak no…"
        />
      </div>

      <LoanTransactionLogCards
        entries={tabEntriesWithBalance}
        showParty={false}
        statementHeader={{
          title: "Transaction history",
          subtitle: `Balance Rs ${fmtRs(balance)}`,
        }}
        emptyMessage={
          logSearch.trim()
            ? "No transactions match your search."
            : activeTab === 2
            ? "No repayments recorded yet."
            : activeTab === 1
            ? "No loans given yet."
            : "No transactions yet."
        }
      />
    </BodyWrapper>
  );
};

export default LoanPartyPortal;
